import { useEffect, useMemo, useState } from 'react';
import {
  App as AntdApp,
  Button,
  Card,
  Dropdown,
  Flex,
  Input,
  Pagination,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  type MenuProps,
  type TableProps,
} from 'antd';
import {
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { leadsApi } from '@/core/api/leadsApi';
import { exportApi } from '@/core/api/exportApi';
import { useAuthStore } from '@/core/auth/authStore';
import { hasPermission } from '@/core/auth/permissions';
import { useSearchParams } from 'react-router';
import type { Lead, LeadPriority, LeadStatus, LeadWithEtag } from '@/core/types/lead';
import { PageHeader } from '@/shared/components/PageHeader';
import { PermissionGuard } from '@/shared/components/PermissionGuard';
import { LeadPriorityTag, LeadStatusTag, getPriorityLabel, getStatusLabel } from '@/shared/components/LeadTags';
import { formatDateTime, fromNow } from '@/core/utils/date';
import { formatNumber, getLeadDisplayName } from '@/core/utils/format';
import { ErrorState } from '@/shared/components/ErrorState';
import { useLeads, leadQueryKeys } from '@/features/leads/hooks/useLeads';
import { LeadFormModal } from '@/features/leads/components/LeadFormModal';
import { LeadDetailsDrawer } from '@/features/leads/components/LeadDetailsDrawer';
import { AssignLeadModal } from '@/features/leads/components/AssignLeadModal';

const statusOptions: Array<{ value: LeadStatus; label: string }> = [
  'new',
  'working',
  'qualified',
  'converted',
  'disqualified',
  'archived',
].map((value) => ({ value: value as LeadStatus, label: getStatusLabel(value as LeadStatus) }));

const priorityOptions: Array<{ value: LeadPriority; label: string }> = [
  'urgent',
  'high',
  'normal',
  'low',
].map((value) => ({ value: value as LeadPriority, label: getPriorityLabel(value as LeadPriority) }));
const leadStatuses = new Set(statusOptions.map((item) => item.value));
const leadPriorities = new Set(priorityOptions.map((item) => item.value));

const parsePage = (value: string | null): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

interface MobileLeadCardProps {
  lead: Lead;
  onOpen: (leadId: string) => void;
  actions: React.ReactNode;
}

const MobileLeadCard = ({ lead, onOpen, actions }: MobileLeadCardProps): React.ReactElement => {
  const phone = lead.mobile || lead.chiefExecutivePhone || lead.contactPersonPhone;

  return (
    <article className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-black text-ink-950">{getLeadDisplayName(lead)}</p>
          <p className="mt-1 truncate text-xs text-slate-400">{lead.companyName || 'مشتری حقیقی'}</p>
        </div>
        <div className="shrink-0">{actions}</div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <LeadStatusTag status={lead.status} />
        <LeadPriorityTag priority={lead.priority} />
        <Tag color={lead.score >= 70 ? 'green' : lead.score >= 40 ? 'gold' : 'default'}>امتیاز {formatNumber(lead.score)}</Tag>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <div className="min-w-0 rounded-xl bg-slate-50 p-3">
          <p className="text-[10px] font-bold text-slate-400">راه ارتباطی</p>
          {phone ? <a className="mt-1 block truncate font-sans font-bold text-brand-600" dir="ltr" href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a> : <p className="mt-1 font-bold text-slate-500">ثبت نشده</p>}
          {lead.email ? <p className="mt-1 truncate font-sans text-[10px] text-slate-400" dir="ltr">{lead.email}</p> : null}
        </div>
        <div className="min-w-0 rounded-xl bg-slate-50 p-3">
          <p className="text-[10px] font-bold text-slate-400">پیگیری</p>
          <p className="mt-1 truncate font-bold text-slate-600">{lead.ownerMembershipId ? 'مسئول تخصیص یافته' : 'بدون مسئول'}</p>
          <p className="mt-1 text-[10px] text-slate-400">{fromNow(lead.createdAtUtc)}</p>
        </div>
      </div>

      <button className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-50 px-4 py-3 text-sm font-black text-brand-600" onClick={() => onOpen(lead.id)} type="button">
        مشاهده و پیگیری سرنخ
        <EyeOutlined />
      </button>
    </article>
  );
};

export const LeadsPage = (): React.ReactElement => {
  const { message } = AntdApp.useApp();
  const queryClient = useQueryClient();
  const permissions = useAuthStore((state) => state.permissions);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [createOpen, setCreateOpen] = useState(searchParams.get('create') === '1');
  const [editLead, setEditLead] = useState<LeadWithEtag | null>(null);
  const [assignLead, setAssignLead] = useState<LeadWithEtag | null>(null);
  const [exporting, setExporting] = useState(false);

  const page = parsePage(searchParams.get('page'));
  const statusParam = searchParams.get('status') as LeadStatus | null;
  const priorityParam = searchParams.get('priority') as LeadPriority | null;
  const status = statusParam && leadStatuses.has(statusParam) ? statusParam : undefined;
  const priority = priorityParam && leadPriorities.has(priorityParam) ? priorityParam : undefined;
  const unassignedOnly = searchParams.get('unassigned') === '1';
  const search = searchParams.get('search') || undefined;
  const selectedLeadId = searchParams.get('lead');

  useEffect(() => {
    setSearchInput(searchParams.get('search') || '');
    if (searchParams.get('create') === '1') setCreateOpen(true);
  }, [searchParams]);

  const query = useMemo(
    () => ({
      page,
      pageSize: 20,
      status,
      priority,
      unassigned: unassignedOnly ? true : undefined,
      search,
    }),
    [page, priority, search, status, unassignedOnly],
  );
  const leadsQuery = useLeads(query);
  const visibleLeads = leadsQuery.data?.items || [];

  const fetchCurrentLead = (leadId: string): Promise<LeadWithEtag> => queryClient.fetchQuery({
    queryKey: leadQueryKeys.detail(leadId),
    queryFn: () => leadsApi.get(leadId),
    staleTime: 0,
  });

  const beginEdit = async (leadId: string): Promise<void> => {
    try {
      setEditLead(await fetchCurrentLead(leadId));
    } catch {
      message.error('دریافت نسخه به‌روز سرنخ ناموفق بود.');
    }
  };

  const beginAssign = async (leadId: string): Promise<void> => {
    try {
      setAssignLead(await fetchCurrentLead(leadId));
    } catch {
      message.error('دریافت نسخه به‌روز سرنخ ناموفق بود.');
    }
  };

  const updateParams = (updates: Record<string, string | null>, resetPage = true): void => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    if (resetPage) next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const openLead = (leadId: string): void => updateParams({ lead: leadId }, false);
  const closeLead = (): void => updateParams({ lead: null }, false);

  const handleSaved = (result: LeadWithEtag, createAnother = false): void => {
    queryClient.setQueryData(leadQueryKeys.detail(result.lead.id), result);
    void queryClient.invalidateQueries({ queryKey: leadQueryKeys.all });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    if (!createAnother) openLead(result.lead.id);
  };

  const closeCreate = (): void => {
    setCreateOpen(false);
    if (searchParams.has('create')) updateParams({ create: null }, false);
  };

  const resetFilters = (): void => {
    setSearchInput('');
    const next = new URLSearchParams();
    if (selectedLeadId) next.set('lead', selectedLeadId);
    setSearchParams(next, { replace: true });
  };

  const renderActions = (lead: Lead): React.ReactElement => {
    const items: MenuProps['items'] = [
      { key: 'view', icon: <EyeOutlined />, label: 'مشاهده جزئیات' },
      hasPermission(permissions, 'leads.update')
        ? { key: 'edit', icon: <EditOutlined />, label: 'ویرایش' }
        : null,
      hasPermission(permissions, 'leads.assign')
        ? { key: 'assign', icon: <UserSwitchOutlined />, label: 'تخصیص مسئول' }
        : null,
    ];

    return (
      <Dropdown
        menu={{
          items,
          onClick: async ({ key, domEvent }) => {
            domEvent.stopPropagation();
            if (key === 'view') openLead(lead.id);
            if (key === 'edit' || key === 'assign') {
              if (key === 'edit') await beginEdit(lead.id);
              if (key === 'assign') await beginAssign(lead.id);
            }
          },
        }}
        trigger={['click']}
      >
        <Button aria-label={`عملیات ${getLeadDisplayName(lead)}`} type="text" icon={<MoreOutlined />} onClick={(event) => event.stopPropagation()} />
      </Dropdown>
    );
  };

  const columns: TableProps<Lead>['columns'] = [
    {
      title: 'مشتری / سازمان',
      key: 'customer',
      fixed: 'left',
      width: 230,
      render: (_, lead) => (
        <div>
          <Typography.Text strong>{getLeadDisplayName(lead)}</Typography.Text>
          <br />
          <Typography.Text type="secondary" className="table-subtitle">
            {lead.companyName || lead.mobile || lead.chiefExecutivePhone || lead.email || 'اطلاعات تکمیلی ندارد'}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'راه ارتباطی',
      key: 'contact',
      width: 180,
      responsive: ['md'],
      render: (_, lead) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text dir="ltr">{lead.mobile || lead.chiefExecutivePhone || '—'}</Typography.Text>
          <Typography.Text dir="ltr" type="secondary" className="table-subtitle">
            {lead.email || '—'}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      width: 130,
      render: (value: LeadStatus) => <LeadStatusTag status={value} />,
    },
    {
      title: 'اولویت',
      dataIndex: 'priority',
      width: 100,
      render: (value: LeadPriority) => <LeadPriorityTag priority={value} />,
    },
    {
      title: 'امتیاز',
      dataIndex: 'score',
      width: 90,
      sorter: (left, right) => left.score - right.score,
      render: (value: number) => (
        <Tag color={value >= 70 ? 'green' : value >= 40 ? 'gold' : 'default'}>{formatNumber(value)}</Tag>
      ),
    },
    {
      title: 'منبع',
      dataIndex: 'source',
      width: 150,
      responsive: ['lg'],
      render: (value: string | null) => value || '—',
    },
    {
      title: 'مسئول',
      dataIndex: 'ownerMembershipId',
      width: 140,
      responsive: ['lg'],
      render: (value: string | null) =>
        value ? <Tooltip title={value}><Tag color="blue">تخصیص‌یافته</Tag></Tooltip> : <Tag color="orange">بدون مسئول</Tag>,
    },
    {
      title: 'تاریخ ثبت',
      dataIndex: 'createdAtUtc',
      width: 140,
      responsive: ['md'],
      render: (value: string) => (
        <Tooltip title={formatDateTime(value)}>
          <Typography.Text type="secondary">{fromNow(value)}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 58,
      render: (_, lead) => renderActions(lead),
    },
  ];

  if (leadsQuery.isError) {
    return <ErrorState error={leadsQuery.error} onRetry={() => void leadsQuery.refetch()} />;
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="مدیریت سرنخ‌ها"
        description="جست‌وجو، ثبت، ویرایش و تخصیص سرنخ‌ها با رعایت محدوده دسترسی هر کاربر"
        extra={
          <>
            {hasPermission(permissions, 'tenancy.manage') ? (
              <Button
                icon={<DownloadOutlined />}
                loading={exporting}
                onClick={async () => {
                  setExporting(true);
                  try { await exportApi.leads(); message.success('فایل Excel سرنخ‌ها آماده شد.'); }
                  catch { message.error('دریافت فایل Excel سرنخ‌ها ناموفق بود.'); }
                  finally { setExporting(false); }
                }}
              >
                خروجی Excel
              </Button>
            ) : null}
            <Button
              icon={<ReloadOutlined />}
              loading={leadsQuery.isFetching}
              onClick={() => void leadsQuery.refetch()}
            >
              بروزرسانی
            </Button>
            <PermissionGuard permission="leads.create">
              <Button data-guide-target="lead-create" type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                سرنخ جدید
              </Button>
            </PermissionGuard>
          </>
        }
      />

      <Card variant="borderless" className="filter-card">
        <Flex gap={12} wrap="wrap" align="center">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="نام، شرکت، موبایل یا ایمیل"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onPressEnter={() => updateParams({ search: searchInput.trim() || null })}
            className="lead-search-input"
          />
          <Button icon={<SearchOutlined />} onClick={() => updateParams({ search: searchInput.trim() || null })}>
            جست‌وجو
          </Button>
          <Select
            allowClear
            placeholder="همه وضعیت‌ها"
            value={status}
            options={statusOptions}
            onChange={(value) => updateParams({ status: value || null })}
            className="lead-filter-select"
          />
          <Select
            allowClear
            placeholder="همه اولویت‌ها"
            value={priority}
            options={priorityOptions}
            onChange={(value) => updateParams({ priority: value || null })}
            className="lead-filter-select"
          />
          <Space>
            <Switch
              checked={unassignedOnly}
              onChange={(checked) => updateParams({ unassigned: checked ? '1' : null })}
            />
            <Typography.Text>فقط بدون مسئول</Typography.Text>
          </Space>
          {(search || status || priority || unassignedOnly) ? (
            <Button type="link" icon={<FilterOutlined />} onClick={resetFilters}>
              پاک‌کردن فیلترها
            </Button>
          ) : null}
        </Flex>
      </Card>

      <Card variant="borderless" className="data-card">
        <Flex justify="space-between" align="center" gap={12} wrap="wrap" className="table-toolbar">
          <Space>
            <Typography.Title level={4}>فهرست سرنخ‌ها</Typography.Title>
            <Tag>{formatNumber(leadsQuery.data?.totalCount || 0)} سرنخ</Tag>
          </Space>
          <Typography.Text type="secondary">
            برای مشاهده جزئیات سرنخ، روی ردیف کلیک کنید.
          </Typography.Text>
        </Flex>

        <div className="grid gap-3 md:hidden">
          {leadsQuery.isLoading ? <div className="h-64 animate-pulse rounded-2xl bg-slate-100" /> : null}
          {!leadsQuery.isLoading && visibleLeads.length === 0 ? <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-12 text-center text-sm text-slate-400">سرنخی مطابق فیلترها پیدا نشد.</div> : null}
          {!leadsQuery.isLoading ? visibleLeads.map((lead) => <MobileLeadCard actions={renderActions(lead)} key={lead.id} lead={lead} onOpen={openLead} />) : null}
          {!leadsQuery.isLoading && (leadsQuery.data?.totalCount || 0) > 20 ? <div className="flex justify-center pt-2"><Pagination current={page} pageSize={20} responsive showLessItems showSizeChanger={false} total={leadsQuery.data?.totalCount || 0} onChange={(nextPage) => updateParams({ page: nextPage > 1 ? String(nextPage) : null }, false)} /></div> : null}
        </div>

        <div className="hidden md:block">
          <Table
            rowKey="id"
            columns={columns}
            dataSource={visibleLeads}
            loading={leadsQuery.isLoading}
            scroll={{ x: 1200 }}
            pagination={{
              current: page,
              pageSize: 20,
              total: leadsQuery.data?.totalCount || 0,
              showSizeChanger: false,
              showTotal: (total) => `${formatNumber(total)} سرنخ`,
              onChange: (nextPage) => updateParams({ page: nextPage > 1 ? String(nextPage) : null }, false),
            }}
            onRow={(lead) => ({ onClick: () => openLead(lead.id) })}
            className="clickable-table"
            locale={{ emptyText: 'سرنخی مطابق فیلترها پیدا نشد.' }}
          />
        </div>
      </Card>

      <LeadDetailsDrawer
        leadId={selectedLeadId}
        onClose={closeLead}
        onEdit={(lead) => { closeLead(); void beginEdit(lead.lead.id); }}
        onAssign={(lead) => { closeLead(); void beginAssign(lead.lead.id); }}
      />
      <LeadFormModal
        open={createOpen}
        mode="create"
        onClose={closeCreate}
        onSaved={handleSaved}
      />
      <LeadFormModal
        open={Boolean(editLead)}
        mode="edit"
        initial={editLead}
        onClose={() => setEditLead(null)}
        onSaved={handleSaved}
      />
      <AssignLeadModal
        open={Boolean(assignLead)}
        lead={assignLead}
        onClose={() => setAssignLead(null)}
        onSaved={handleSaved}
      />
    </div>
  );
};
